<template>
  <section class="page page-products">
    <article
      class="designs-page"
      :class="{ 'left-page': !isEvenPage, 'right-page': isEvenPage }"
    >
      <h2 class="title forza-bold">{{ catalogName }}</h2>
      <div class="designs-container">
        <div class="hero-design">
          <Product
            v-if="product && pageIsReady"
            :key="'hero-' + product && product.id"
            :hideInfo="true"
            :originalProduct="product"
            :designName="heroDesignName"
            :withImage="true"
            :productIsCustomizable="true"
            :readOnly="readOnly"
          />
        </div>
        <div class="other-designs" v-if="pageIsReady">
          <Product
            v-for="(design, index) in otherDesigns"
            :key="index + '-' + design"
            :originalProduct="product"
            :designName="design"
            :hideInfo="true"
            :withImage="true"
            :readOnly="readOnly"
          />

          <div class="button-container" v-if="!readOnly">
            <button
              class="btn forza-bold"
              @click="setDesignsModalProduct(product)"
            >
              VIEW ALL DESIGNS
            </button>
          </div>
        </div>
      </div>
    </article>
    <article id="product-info">
      <div class="info-container">
        <Product
          v-if="product"
          :key="'hero-info-' + product && product.id"
          :originalProduct="product"
          :designName="heroDesignName"
          :priceIsEditable="true"
          :productIsCustomizable="false"
          :withImage="false"
          :withBranding="true"
          :withDescription="true"
          :withProductGrid="true"
        />
      </div>
      <div class="info-text">
        <h3 class="title forza-bold text-grey-dark">
          {{ title }}
        </h3>
        <p class="text-grey-dark">
          {{ pageDescription }}
        </p>
      </div>
    </article>
    <div
      class="page-number-count"
      :class="{ 'right-page-number': pageId % 2 === 0 }"
    >
      {{ pageId }}
    </div>
  </section>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import Product from "@/components/Projects/Product/Product.vue";

export default {
  name: "SublimatedPage",

  props: ["page", "pageId", "readOnly"],

  components: { Product },

  computed: {
    ...mapGetters("catalog", ["getProductCatalogData"]),

    background() {
      return this.isEvenPage
        ? "https://fsg-static-repo.netlify.app/images/basketball-program/chromagear_bg_right.png"
        : "https://fsg-static-repo.netlify.app/images/basketball-program/chromagear_bg_left.png";
    },

    catalogName() {
      return this.page?.catalog;
    },

    heroDesignName() {
      if (this.productData) {
        return this.productData?.hero;
      } else {
        return this.product?.threekit_designs?.[0]?.design_name;
      }
    },

    isEvenPage() {
      return this.pageId % 2 === 0;
    },

    otherDesigns() {
      if (this.productData) {
        const designsKeys = Object.keys(this.productData)?.filter((key) =>
          key?.includes("design")
        );

        const designsNames = designsKeys.map(
          (designKey) => this.productData?.[designKey]
        );

        return designsNames;
      } else {
        return this.product?.threekit_designs
          ?.slice(1, 7)
          ?.map((d) => d?.design_name);
      }
    },

    pageDescription() {
      return !this.isEvenPage
        ? "Top quality, cut and sew construction from North America's leader in custom sub. No up-charges for Names, Numbers, Logos and badges."
        : "Designs embedded into the fabric for lightweight, never-fail performance. Colors and logos never crack, peel or fade.";
    },

    product() {
      return this.page?.products?.[0];
    },

    productData() {
      return this.getProductCatalogData(this.product?.styleId);
    },

    title() {
      return !this.isEvenPage
        ? "The best sublimation in the industry"
        : "Step up to Chromagear custom sublimation";
    },
  },

  methods: {
    ...mapActions("project", ["setDesignsModalProduct"]),
  },

  mounted() {
    //make a loader for sidebar, which is when this is read only
    const delay = this.pageId || 1;
    if (!this.pageIsReady) {
      setTimeout(() => {
        this.pageIsReady = true;
      }, 2000 * delay);
    }
  },

  data() {
    return {
      pageIsReady: !this.readOnly,
    };
  },
};
</script>

<style lang="scss" scoped>

.designs-page {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 60%;
  padding: 1em;
  padding-left: 1.25em;
  padding-right: 1.75em;
  background: url("https://fsg-static-repo.netlify.app/images/basketball-program/chromagear_bg_left.png")
      left top no-repeat,
    url("https://fsg-static-repo.netlify.app/images/basketball-program/chromagear_bg_right.png")
      right top no-repeat;
  background-repeat: no-repeat;
  background-size: contain;

  &.right-page {
    .title {
      margin-left: auto;
    }
  }

  .designs-container {
    display: flex;
    justify-content: space-between;
    height: 88%;
    gap: 1em;
    .hero-design {
      width: 60%;
    }
    .other-designs {
      display: grid;
      align-items: end;
      column-gap: 0.75em;
      row-gap: 3%;
      grid-template-columns: repeat(2, 50%);
      grid-template-rows: 27% 27% 27% 9%;
      width: 35%;
      height: 100%;

      .button-container {
        display: flex;
        grid-column-start: 1;
        grid-column-end: 3;
        align-items: flex-end;
        .btn {
          background: $grey-lightest;
          color: $dark;
          border: 1px solid $dark;
          width: 100%;
          height: 2.5em;
          // margin-top: .25em;
        }
      }
    }

    ::v-deep .product {
      background: white;
      border-radius: 6px;
      border: $grey-light 1px solid;

      .product-preview {
        justify-content: center;
      }
    }
  }
}

#product-info {
  display: flex;
  height: 40%;
  padding: 2em;
  padding-top: 1.25em;

  .info-container {
    width: 60%;
    padding-right: 1em;
    padding-left: 1.5em;
  }

  .info-text {
    width: 40%;
    height: 100%;
    padding: 1em;
    padding-left: 1.15em;
    padding-top: 0;
    border-left: 1px solid $grey-light;

    h3 {
      margin-bottom: 1em;
      font-size: 1.35em;
    }
    p {
      width: 80%;
      line-height: 1.75em;
    }
  }
}
</style>
